import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { BaseTag, TagCategory } from '@sleipai/sleip-models';
import * as TagActions from '../actions/tag.actions';
import * as TagReducers from '../reducers/tag.reducer';
import * as TagSelectors from '../selectors/tag.selectors';

@Injectable({
  providedIn: 'root',
})
export class TagStoreFacade {
  private store = inject(Store<TagReducers.State>);

  readonly tags$ = this.store.pipe(select(TagSelectors.selectTags));
  readonly loading$ = this.store.pipe(select(TagSelectors.selectIsLoading));
  readonly loaded$ = this.store.pipe(select(TagSelectors.selectIsLoaded));
  readonly error$ = this.store.pipe(select(TagSelectors.selectError));

  readonly tags = toSignal(this.tags$, {
    initialValue: {} as { [category: string]: { [id: string]: BaseTag } },
  });

  readonly loaded = toSignal(this.loaded$, { initialValue: false });

  constructor() {
    this.ensureLoaded();
  }

  getTag<T extends BaseTag = BaseTag>(
    categoryOrRef:
      | { category?: TagCategory | string | null; id?: string | null }
      | TagCategory
      | string
      | null
      | undefined,
    tagId?: string | null
  ): T | null {
    if (!categoryOrRef) return null;
    if (typeof categoryOrRef === 'object') {
      const category = categoryOrRef.category;
      const id = categoryOrRef.id;
      if (!category || !id) return null;
      return (this.tags()?.[category]?.[id] as T) ?? null;
    }
    if (!tagId) return null;
    return (this.tags()?.[categoryOrRef]?.[tagId] as T) ?? null;
  }

  getTag$<T extends BaseTag = BaseTag>(
    category: TagCategory | string,
    id: string
  ): Observable<T | null> {
    this.ensureLoaded();
    return this.store.pipe(select(TagSelectors.selectTag<T>(category, id)));
  }

  getTags<T extends BaseTag = BaseTag>(category: TagCategory | string): T[] {
    const categoryTags = this.tags()?.[category];
    return categoryTags ? (Object.values(categoryTags) as T[]) : [];
  }

  getTags$<T extends BaseTag = BaseTag>(
    category: TagCategory | string
  ): Observable<T[]> {
    this.ensureLoaded();
    return this.store.pipe(select(TagSelectors.selectTagsByCategory<T>(category)));
  }

  ensureLoaded(): void {
    combineLatest([
      this.store.pipe(select(TagSelectors.selectIsLoaded)),
      this.store.pipe(select(TagSelectors.selectIsLoading)),
      this.store.pipe(select(TagSelectors.selectError)),
    ])
      .pipe(take(1))
      .subscribe(([loaded, loading, error]) => {
        if (!loaded && !loading && !error) {
          this.store.dispatch(TagActions.loadTags());
        }
      });
  }
}
